import React from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { mergeContacts, searchContacts, type ContactSummarySearchResult } from '../api/contacts'

interface MergeContactsModalProps {
  open: boolean
  stationId: number
  fromContactId: number | null
  onClose: () => void
  onMerged?: (intoContactId: number) => void
}

const MergeContactsModal: React.FC<MergeContactsModalProps> = ({
  open,
  stationId,
  fromContactId,
  onClose,
  onMerged,
}) => {
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState<ContactSummarySearchResult[]>([])
  const [selected, setSelected] = React.useState<ContactSummarySearchResult | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const fetchContacts = React.useCallback(
    async (value: string) => {
      try {
        setLoading(true)
        const response = await searchContacts(stationId, value)
        const filtered = response.items.filter((contact) => contact.id !== fromContactId)
        setResults(filtered)
        setSelected(null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to search contacts.'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [stationId, fromContactId],
  )

  React.useEffect(() => {
    if (!open) {
      return
    }
    setError(null)
    void fetchContacts('')
  }, [open, fetchContacts])

  const handleSubmit = async () => {
    if (!fromContactId || !selected) {
      return
    }

    try {
      setLoading(true)
      const result = await mergeContacts({
        station_id: stationId,
        from_contact_id: fromContactId,
        to_contact_id: selected.id,
      })
      onMerged?.(result.to_contact_id)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to merge contacts.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    void fetchContacts(value)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Merge contacts</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            Merging contacts moves all identities and history into the selected contact. This action cannot be undone.
          </Alert>
          <TextField
            label="Search contacts"
            placeholder="Search for the destination contact"
            value={query}
            onChange={handleSearchChange}
            fullWidth
            autoFocus
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Divider />
          <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
            {results.map((contact) => (
              <ListItemButton key={contact.id} selected={selected?.id === contact.id} onClick={() => setSelected(contact)}>
                <ListItemText
                  primary={contact.name || '(unnamed contact)'}
                  secondary={contact.location || 'No location specified'}
                />
              </ListItemButton>
            ))}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No contacts available to merge into.
              </Typography>
            )}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selected || loading || !fromContactId}>
          Merge into selected contact
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MergeContactsModal

